/* ==========================================================================
   sd-motion.js - the single motion layer
   --------------------------------------------------------------------------
   THE ONE RULE THAT CHANGED, AND WHY

   The previous build put `opacity: 0` on every [data-sd-reveal] element the
   moment a class landed on <html>. On /collections/dof-reality that measured
   20 of 24 product cards at computed opacity 0, plus the product description
   and the shipping-and-returns block on every PDP. A slow GSAP fetch, a
   blocked CDN, a missed ScrollTrigger refresh or a stray console error all
   produced the same result: a blank shop.

   This file cannot do that any more. There is no global hiding rule left in
   the CSS. Reveal pre-states are applied per element, by this script, AFTER
   GSAP is confirmed ready, and ONLY to elements measured to be below the fold
   at that moment. Everything already on screen is animated with transform
   only, or not at all.

   Concretely:
     - every reveal tween sets immediateRender: false, so the "from" state is
       never written until the ScrollTrigger actually starts the tween
     - elements whose top is already above 88% of the viewport get no tween
     - if GSAP never arrives, nothing was ever hidden, so there is nothing to
       recover: the page is already complete

   THE CONTRACT
     data-sd-reveal            fade and rise once, on enter
     data-sd-reveal="x"        the same, sliding in from the left
     data-sd-reveal-group      container; its [data-sd-reveal] children stagger
     data-sd-marquee           rail; its first child is the track to drift
     data-sd-spine             a build-stage spine; fills as it scrolls past
     data-sd-spine-fill        the element that scales
     data-sd-spine-node        a node on that spine; lights when reached
     data-sd-parallax="0.12"   subtle drift. Media only, never text.
     data-sd-count             animates a measured number up to its own value

     window.sdMotionReady      (v4) a Promise that resolves to
                               { gsap, ScrollTrigger } once both are loaded and
                               registered (the same moment html.sd-motion-on
                               lands and sd:motion-ready fires), or to null when
                               motion will not run on this page view: reduced
                               motion, Save-Data / slow connection, the visitor's
                               stored pause, settings.sd_motion_enabled off, or a
                               load failure. It never rejects and never stays
                               pending forever (20 s safety resolve to null).
     window.sdMotionBlockedBy()  'reduced' | 'data' | 'user' | null
     localStorage 'sd-motion'  'off' = the visitor pressed Pause motion
                               (sd-v4.js writes it). The next page view loads
                               no GSAP at all. Removing the key restores motion.
     window.sdMotionPaused     true while paused on this page view (sd-v4.js);
                               the brand marquee stops drifting.

   PAYLOAD
     This file ~10 KB. GSAP + ScrollTrigger 116 KB, fetched lazily, after first
     paint, never on the critical path, and never for a visitor who asked for
     reduced motion or is on Save-Data.

   NOTE FOR ANYONE SCREENSHOTTING THIS THEME
     GSAP's requestAnimationFrame ticker never lets headless Chrome's
     --virtual-time-budget elapse. Capture without that flag.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = document.documentElement;

  /* ---------------------------------------------------------------------
     Ready promise (v4). Created before the gate so every page view gets
     one, and resolved exactly once: with the libraries, or with null.
     --------------------------------------------------------------------- */
  var resolveReady;
  window.sdMotionReady = new Promise(function (r) { resolveReady = r; });

  /* ---------------------------------------------------------------------
     Gate: should motion run at all?
     --------------------------------------------------------------------- */
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onExpensiveConnection() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || '';
    return t === 'slow-2g' || t === '2g' || t === '3g';
  }

  // The visitor's own Pause motion choice, stored by sd-v4.js.
  function userPaused() {
    try { return localStorage.getItem('sd-motion') === 'off'; } catch (e) { return false; }
  }

  // Exposed so sd-frames.js asks the same question rather than inventing its own.
  window.sdMotionAllowed = function () {
    return !prefersReducedMotion() && !onExpensiveConnection() && !userPaused();
  };

  window.sdMotionBlockedBy = function () {
    return prefersReducedMotion() ? 'reduced' : onExpensiveConnection() ? 'data' : userPaused() ? 'user' : null;
  };

  if (!window.sdMotionAllowed()) { resolveReady(null); return; }

  /* ---------------------------------------------------------------------
     Token bridge. Durations and easing live in sd-tokens.css, so changing the
     feel of the site is a CSS edit, not a JavaScript edit.
     --------------------------------------------------------------------- */
  function cssMs(name, fallback) {
    var raw = getComputedStyle(ROOT).getPropertyValue(name).trim();
    if (!raw) return fallback;
    var n = parseFloat(raw);
    if (isNaN(n)) return fallback;
    return raw.indexOf('ms') > -1 ? n / 1000 : n;
  }

  function cssPx(name, fallback) {
    var raw = getComputedStyle(ROOT).getPropertyValue(name).trim();
    var n = parseFloat(raw);
    return isNaN(n) ? fallback : n;
  }

  var T = {
    base: cssMs('--sdp-dur-base', 0.24),
    slow: cssMs('--sdp-dur-slow', 0.52),
    stagger: cssMs('--sdp-stagger', 0.055),
    rise: cssPx('--sdp-rise', 16),
    // Damped. No bounce, no elastic, no overshoot. Motorsport hardware does
    // not boing, and neither does the page that sells it.
    ease: 'power3.out'
  };

  /* ---------------------------------------------------------------------
     Lazy loader with a pinned, integrity-checked CDN fallback. GSAP is
     vendored into assets/ so the theme has no third-party runtime dependency,
     but a theme asset can be truncated by a bad upload, and that fails
     silently. Same 3.13.0 builds, verified by SRI.
     --------------------------------------------------------------------- */
  var FALLBACK = {
    gsap: {
      url: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/gsap.min.js',
      sri: 'sha384-HOvlOYPIs/zjoIkWUGXkVmXsjr8GuZLV+Q+rcPwmJOVZVpvTSXQChiN4t9Euv9Vc'
    },
    st: {
      url: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/ScrollTrigger.min.js',
      sri: 'sha384-P8VzCVnT9NBUkMrpcIZrJbA7EBjJvh/fJS6PmP+4nLIM284DtsImIv8D0fFjIkeh'
    }
  };

  var loaded = {};
  function loadScript(src, integrity) {
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.defer = true;
      if (integrity) {
        s.integrity = integrity;
        s.crossOrigin = 'anonymous';
      }
      s.onload = resolve;
      s.onerror = function () { reject(new Error('sd-motion: failed to load ' + src)); };
      document.head.appendChild(s);
    });
    return loaded[src];
  }

  function loadWithFallback(primary, fb, globalName) {
    return loadScript(primary)
      .then(function () {
        if (window[globalName]) return true;
        throw new Error('sd-motion: ' + globalName + ' missing after load');
      })
      .catch(function () { return loadScript(fb.url, fb.sri); });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() {
    var gsapUrl = ROOT.getAttribute('data-sd-gsap');
    var stUrl = ROOT.getAttribute('data-sd-scrolltrigger');
    if (!gsapUrl || !stUrl) { resolveReady(null); return; } // layout did not declare the assets; stay static.

    // A stalled fetch must not leave sdMotionReady pending forever. A promise
    // settles once, so a normal resolve before this fires wins.
    setTimeout(function () { resolveReady(null); }, 20000);

    // Hero drift from the image's own load event: never before it paints. A
    // complete image has painted, so it is left alone.
    document.querySelectorAll('img.sd-hero__img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () { img.classList.add('sd-hero__img--in'); }, { once: true });
    });

    loadWithFallback(gsapUrl, FALLBACK.gsap, 'gsap')
      .then(function () { return loadWithFallback(stUrl, FALLBACK.st, 'ScrollTrigger'); })
      .then(function () {
        if (!window.gsap || !window.ScrollTrigger) { resolveReady(null); return; }
        window.gsap.registerPlugin(window.ScrollTrigger);
        ROOT.classList.add('sd-motion-on'); // a hook for CSS. It hides nothing.
        init(window.gsap, window.ScrollTrigger);
        resolveReady({ gsap: window.gsap, ScrollTrigger: window.ScrollTrigger });
        document.dispatchEvent(new Event('sd:motion-ready'));
      })
      .catch(function (err) {
        // Nothing to undo. Nothing was ever hidden.
        resolveReady(null);
        if (window.console && console.warn) console.warn(err.message);
      });
  }

  /* ---------------------------------------------------------------------
     Behaviours
     --------------------------------------------------------------------- */
  function init(gsap, ScrollTrigger) {
    gsap.defaults({ ease: T.ease, duration: T.slow });

    var vh = window.innerHeight || 800;

    // An element is a reveal candidate only if it is genuinely below the fold
    // right now. Anything on screen keeps its final state and is never touched,
    // which is what makes a late-arriving GSAP harmless instead of destructive.
    function belowFold(el) {
      var r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false; // display:none, hidden tab
      return r.top > vh * 0.88;
    }

    // --- 1. Grouped stagger. Run first so grouped children are claimed before
    //        the loose pass sees them.
    var claimed = new WeakSet();

    document.querySelectorAll('[data-sd-reveal-group]').forEach(function (group) {
      var all = group.querySelectorAll('[data-sd-reveal]');
      if (!all.length) return;

      var items = [];
      all.forEach(function (el) {
        claimed.add(el);
        if (belowFold(el)) items.push(el);
      });
      if (!items.length) return;

      gsap.fromTo(
        items,
        { opacity: 0, y: T.rise },
        {
          opacity: 1,
          y: 0,
          duration: T.slow,
          stagger: T.stagger,
          overwrite: 'auto',
          immediateRender: false,
          clearProps: 'opacity,transform',
          scrollTrigger: { trigger: group, start: 'top 90%', once: true }
        }
      );
    });

    // --- 2. Loose reveals. "x" arrives from the left (the stage index).
    document.querySelectorAll('[data-sd-reveal]').forEach(function (el) {
      if (claimed.has(el)) return;
      if (!belowFold(el)) return;

      gsap.fromTo(
        el,
        el.getAttribute('data-sd-reveal') === 'x' ? { opacity: 0, x: -T.rise } : { opacity: 0, y: T.rise },
        {
          opacity: 1,
          x: 0,
          y: 0,
          duration: T.slow,
          overwrite: 'auto',
          immediateRender: false,
          clearProps: 'opacity,transform',
          scrollTrigger: { trigger: el, start: 'top 92%', once: true }
        }
      );
    });

    // --- 3. The build spine. A hairline down the five stage bands whose fill
    //        tracks scroll position, so the page traces the order a rig is
    //        actually assembled in. This is the signature motion of the theme;
    //        everything else is a quiet fade. It animates a decorative element
    //        only - no content depends on it.
    document.querySelectorAll('[data-sd-spine]').forEach(function (spine) {
      var fill = spine.querySelector('[data-sd-spine-fill]');
      var nodes = spine.querySelectorAll('[data-sd-spine-node]');

      if (fill) {
        gsap.fromTo(
          fill,
          { scaleY: 0 },
          {
            scaleY: 1,
            ease: 'none',
            transformOrigin: 'top center',
            scrollTrigger: { trigger: spine, start: 'top 62%', end: 'bottom 80%', scrub: 0.4 }
          }
        );
      }

      nodes.forEach(function (node) {
        ScrollTrigger.create({
          trigger: node,
          start: 'top 68%',
          onEnter: function () { node.setAttribute('data-sd-lit', 'true'); },
          onLeaveBack: function () { node.removeAttribute('data-sd-lit'); }
        });
      });
    });

    // --- 4. Parallax. Transform only, media only, capped so it stays a drift.
    document.querySelectorAll('[data-sd-parallax]').forEach(function (el) {
      var amount = parseFloat(el.getAttribute('data-sd-parallax')) || 0.1;
      amount = Math.max(-0.25, Math.min(0.25, amount));
      gsap.fromTo(
        el,
        { yPercent: -amount * 100 },
        {
          yPercent: amount * 100,
          ease: 'none',
          scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true }
        }
      );
    });

    // --- 5. Counters. The number is already in the DOM and is the truth; this
    //        only counts up to it. If the trigger never fires the real figure
    //        is what stays on screen.
    document.querySelectorAll('[data-sd-count]').forEach(function (el) {
      var text = el.textContent || '';
      var m = text.match(/^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/);
      if (!m) return;
      var target = parseFloat(m[2].replace(/,/g, ''));
      if (!isFinite(target) || target <= 0) return;
      var proxy = { v: 0 };

      gsap.to(proxy, {
        v: target,
        duration: 1.1,
        ease: 'power2.out',
        immediateRender: false,
        scrollTrigger: { trigger: el, start: 'top 92%', once: true },
        onUpdate: function () { el.textContent = m[1] + Math.round(proxy.v).toLocaleString() + m[3]; },
        onComplete: function () { el.textContent = text; }
      });
    });

    // --- 6. Brand marquee. The rail drifts by scrolling ITSELF, so a wheel, a
    //        trackpad swipe or a finger simply takes the same scroll position
    //        over; the drift resumes a few seconds after the hand leaves. The
    //        row is cloned once so the loop closes without a seam. Clones are
    //        hidden from AT and out of the tab order.
    var reducedDrift = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('[data-sd-marquee]').forEach(function (rail) {
      var track = rail.firstElementChild;
      if (!track || track.children.length < 2) return;

      Array.prototype.slice.call(track.children).forEach(function (item) {
        var copy = item.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a, button').forEach(function (a) { a.tabIndex = -1; });
        track.appendChild(copy);
      });
      rail.setAttribute('data-sd-marquee-on', '');
      if (reducedDrift) return;

      var speed = window.matchMedia('(hover: hover)').matches ? 40 : 26; // px per second
      var hold = 0, hovering = false, last = null, expected = rail.scrollLeft;
      // Off screen, the row holds still (WCAG 2.2.2: autoplay stops when it is
      // not being looked at). Without IntersectionObserver it behaves as before.
      // The frame loop itself only runs while the rail is on screen (it reads
      // the track width every frame, which is a forced layout during load).
      var inView = true, raf = 0;
      function run() { if (!raf) { last = null; raf = requestAnimationFrame(step); } }
      if ('IntersectionObserver' in window) {
        inView = false;
        new IntersectionObserver(function (entries) {
          inView = entries[entries.length - 1].isIntersecting;
          if (inView) run();
        }).observe(rail);
      }
      function pause(ms) { hold = Date.now() + ms; }
      rail.addEventListener('mouseenter', function () { hovering = true; });
      rail.addEventListener('mouseleave', function () { hovering = false; });
      rail.addEventListener('focusin', function () { hovering = true; });
      rail.addEventListener('focusout', function () { hovering = false; });
      ['wheel', 'pointerdown', 'touchstart'].forEach(function (ev) {
        rail.addEventListener(ev, function () { pause(4000); }, { passive: true });
      });
      // A scroll the script did not ask for is a hand on the row.
      rail.addEventListener('scroll', function () {
        if (Math.abs(rail.scrollLeft - expected) > 2) { pause(2500); expected = rail.scrollLeft; }
      }, { passive: true });

      function step(now) {
        raf = 0;
        if (!inView) { last = null; return; }
        if (last == null) last = now;
        var dt = Math.min(64, now - last);
        last = now;
        var half = track.scrollWidth / 2;
        if (half > 0) {
          var x = rail.scrollLeft;
          if (!hovering && Date.now() > hold && !document.hidden && inView && !window.sdMotionPaused) x += speed * dt / 1000;
          if (x >= half) x -= half;
          if (x !== rail.scrollLeft) { expected = x; rail.scrollLeft = x; expected = rail.scrollLeft; }
        }
        raf = requestAnimationFrame(step);
      }
      if (inView) run();
    });

    // The theme editor replaces a section's DOM on every setting change. Those
    // fresh nodes have no tween. Nothing hides them either, so all that is
    // needed is a refresh of the trigger positions.
    document.addEventListener('shopify:section:load', function () { ScrollTrigger.refresh(); });
    document.addEventListener('shopify:section:unload', function () { ScrollTrigger.refresh(); });

    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
